import React from 'react'

// Renders a survey answer-option emoji as a direct image from the same Apple emoji CDN that
// emoji-picker-react uses.
//
// Why not use emoji-picker-react's <Emoji> here: its standalone <Emoji> resolves the codepoint
// through the picker's data context (usePickerDataContext().emojiByUnified) and returns null until
// that dataset has loaded. In preview / participant / dashboard views there is no <EmojiPicker>
// mounted to populate it, so on a fresh client-side navigation the emoji rendered blank and only
// appeared after a hard refresh (once the dataset was cached). Rendering the CDN image directly has
// no data-context dependency, so it paints on first render — while looking identical (same images).
const APPLE_CDN = 'https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/'

// Convert a unified codepoint string ("1f600", or "1f1e6-1f1e8" for multi-part) to its native glyph,
// used only as a fallback if the CDN image fails to load.
const toNativeGlyph = unified => {
  try {
    return unified
      .split('-')
      .map(part => String.fromCodePoint(parseInt(part, 16)))
      .join('')
  } catch {
    return ''
  }
}

export default function SurveyEmoji({unified, size = 32}) {
  if (!unified) return null
  return (
    <img
      src={`${APPLE_CDN}${unified}.png`}
      width={size}
      height={size}
      alt=""
      loading="eager"
      style={{display: 'inline-block', verticalAlign: 'middle'}}
      onError={e => {
        // CDN image unavailable — swap in the OS-native glyph so something still shows.
        const span = document.createElement('span')
        span.style.fontSize = `${size}px`
        span.style.lineHeight = '1'
        span.textContent = toNativeGlyph(unified)
        e.currentTarget.replaceWith(span)
      }}
    />
  )
}
